import { useCallback, useMemo } from 'react'

import { useFirm } from '~/components/hooks/useFirm'
import {
  Feature,
  FirmProfileFragment,
  useFetchFirmFeatureFlags,
  useFetchUserFeatureFlags,
} from '~/graphql/generated/query.types'
import { featureFlagConfigs } from '~/util/featureFlags'

export function useFeatureFlag(feature: Feature) {
  const firm = useFirm()
  const featureFlagsForFirm = useFetchFirmFeatureFlags()
  const featureFlagsForUser = useFetchUserFeatureFlags()

  const isFeatureEnabled = useMemo(
    () => _getIsFeatureEnabledForUser(featureFlagsForFirm, featureFlagsForUser, feature, firm),
    [featureFlagsForFirm, featureFlagsForUser, feature, firm]
  )

  return isFeatureEnabled
}

export function useFeatureFlags(): (feature: Feature) => boolean {
  const firm = useFirm()
  const featureFlagsForFirm = useFetchFirmFeatureFlags()
  const featureFlagsForUser = useFetchUserFeatureFlags()

  const getIsFeatureEnabled = useCallback(
    (feature: Feature) =>
      _getIsFeatureEnabledForUser(featureFlagsForFirm, featureFlagsForUser, feature, firm),
    [featureFlagsForFirm, featureFlagsForUser, firm]
  )
  return getIsFeatureEnabled
}

function _getIsFeatureEnabledForUser(
  featureFlagsForFirm: ReturnType<typeof useFetchFirmFeatureFlags>,
  featureFlagsForUser: ReturnType<typeof useFetchUserFeatureFlags>,
  feature: Feature,
  firm: FirmProfileFragment
): boolean {
  if (firm.isDemo && featureFlagConfigs[feature].forceEnabledForDemoFirms) {
    return true
  }
  const firmFlagIsEnabled =
    featureFlagsForFirm.data?.fetchFirmFeatureFlags.find(f => f.feature === feature)?.isEnabled ??
    false
  if (firmFlagIsEnabled) return true
  const userFlagIsEnabled = featureFlagsForUser.data?.fetchUserFeatureFlags.find(
    f => f.feature === feature
  )?.isEnabled
  return userFlagIsEnabled ?? false
}
